import React, { useMemo, useState } from 'react';
import { X, TrendingDown, TrendingUp } from 'lucide-react';
import { IProjectMetrics, CardData } from '../types';
import { useSimulationSafe } from '../context/SimulationContext';
import { useProjectStore } from '../store/useProjectStore';

interface ComparisonViewProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyMap?: () => void;
  current: IProjectMetrics;
  future: IProjectMetrics;
}

type PendingAction = 'close' | 'apply' | null;

const parseNumeric = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const percentDelta = (before: number, after: number): number => {
  if (!Number.isFinite(before) || Math.abs(before) < 1e-6) return 0;
  return ((after - before) / before) * 100;
};

const formatDuration = (minutes: number): string => {
  const safe = Math.max(0, minutes);
  if (safe < 60) return `${safe.toFixed(1)} min`;
  if (safe < 1440) return `${(safe / 60).toFixed(1)} h`;
  return `${(safe / 1440).toFixed(1)} dias`;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

const trendClass = (isPositive: boolean) => (isPositive ? 'text-emerald-600' : 'text-red-600');

export const ComparisonView: React.FC<ComparisonViewProps> = ({ isOpen, onClose, onApplyMap, current, future }) => {
  const simulation = useSimulationSafe();
  const activeCards = useProjectStore((state) => {
    const activeSheet = state.sheets.find((s) => s.id === state.activeSheetId);
    return activeSheet?.cards ?? [];
  });

  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState(`Proposta Kaizen ${new Date().toLocaleDateString('pt-BR')}`);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  if (!isOpen) return null;

  const currentLeadTime = parseNumeric(current.longestPathTP) || parseNumeric(current.cycleTime) || parseNumeric(current.longestPath);
  const futureLeadTime = parseNumeric(future.longestPathTP) || parseNumeric(future.cycleTime) || parseNumeric(future.longestPath);

  const currentPce = parseNumeric(current.flowEfficiency);
  const futurePce = parseNumeric(future.flowEfficiency);

  const currentCost = Number.isFinite(current.monthlyOpCost) && current.monthlyOpCost > 0
    ? current.monthlyOpCost
    : parseNumeric(current.hiddenFactoryCost);
  const futureCost = Number.isFinite(future.monthlyOpCost) && future.monthlyOpCost > 0
    ? future.monthlyOpCost
    : parseNumeric(future.hiddenFactoryCost);

  const currentCapacity = Math.max(0, parseNumeric(current.capacitySystem));
  const futureCapacity = Math.max(0, parseNumeric(future.capacitySystem));

  const leadDeltaPct = percentDelta(currentLeadTime, futureLeadTime);
  const pceDeltaPts = futurePce - currentPce;
  const costDeltaPct = percentDelta(currentCost, futureCost);
  const capacityDeltaPct = percentDelta(currentCapacity, futureCapacity);

  const monthlySaving = Math.max(0, currentCost - futureCost);
  const annualSaving = monthlySaving * 12;

  const optimizedNodeSnapshot = useMemo(() => {
    return activeCards
      .filter((card: CardData) => card.status === 'ativo' && card.tipo !== 'card_inicial' && card.tipo !== 'card_final')
      .map((card: CardData) => {
        const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? card.p ?? 1));
        const override = simulation?.kaizenOverrides?.[card.id] ?? {};
        const extraFte = Math.max(0, Number((override as any).fte ?? (override as any).extraFTE ?? 0));
        const traMinutes = Math.max(0, Number(card.tt ?? 0));
        const rawTp = Number(card.lt ?? card.tp ?? card.tt ?? 0);
        const tpMinutes = Math.max(traMinutes, Number.isFinite(rawTp) ? rawTp : traMinutes);

        return {
          id: card.id,
          name: card.atividade || card.quem || card.responsavel || 'Etapa',
          traMinutes,
          tpMinutes,
          baseFte,
          extraFte,
          totalFte: baseFte + extraFte,
        };
      });
  }, [activeCards, simulation?.kaizenOverrides]);

  const finalizePendingAction = () => {
    if (pendingAction === 'apply') {
      if (onApplyMap) onApplyMap();
      else onClose();
    } else {
      onClose();
    }
    setSavePromptOpen(false);
    setPendingAction(null);
  };

  const requestSaveConfirmation = (action: PendingAction) => {
    setPendingAction(action);
    setSavePromptOpen(true);
  };

  const handleRegisterMilestone = () => {
    if (!simulation?.saveScenario) {
      finalizePendingAction();
      return;
    }

    const leadTimeGainPercent = Math.max(0, ((currentLeadTime - futureLeadTime) / Math.max(currentLeadTime, 1)) * 100);
    const leadTimeDays = Math.max(0, futureLeadTime / 480);

    simulation.saveScenario(scenarioName.trim() || `Marco ${new Date().toLocaleDateString('pt-BR')}`, {
      isMilestone: true,
      source: 'insight_modal',
      totalLeadTime: leadTimeDays,
      inertiaCost: futureCost,
      leadTimeGainPercent,
      kpis: {
        leadTimeMinutes: Math.max(0, futureLeadTime),
        pcePercent: Math.max(0, futurePce),
        nvaCost: Math.max(0, futureCost),
        deliveryCapacityPerDay: Math.max(0, futureCapacity),
      },
      nodeSnapshot: optimizedNodeSnapshot,
    });

    finalizePendingAction();
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[90vh] border border-slate-300 bg-white shadow-2xl flex flex-col">
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800">RELATÓRIO DE IMPACTO: CENÁRIO OTIMIZADO</h2>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Comparação técnica para decisão executiva</p>
          </div>
          <button onClick={() => requestSaveConfirmation('close')} className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 flex-1 min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto border border-slate-300">
            <table className="w-full border-collapse text-[11px]">
              <thead className="bg-slate-100 text-slate-600 uppercase tracking-wider">
                <tr>
                  <th className="border-r border-slate-200 px-2 py-1 text-left font-semibold">Indicador</th>
                  <th className="border-r border-slate-200 px-2 py-1 text-right font-semibold">Estado Atual</th>
                  <th className="border-r border-slate-200 px-2 py-1 text-right font-semibold">Estado Otimizado</th>
                  <th className="px-2 py-1 text-right font-semibold">Ganho (Δ)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Lead Time Total</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatDuration(currentLeadTime)}</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatDuration(futureLeadTime)}</td>
                  <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                    <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${trendClass(leadDeltaPct <= 0)}`}>
                      {leadDeltaPct <= 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                      {`${leadDeltaPct > 0 ? '+' : ''}${leadDeltaPct.toFixed(1)}%`}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Eficiência de Fluxo (PCE)</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatPercent(currentPce)}</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatPercent(futurePce)}</td>
                  <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                    <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${trendClass(pceDeltaPts >= 0)}`}>
                      {pceDeltaPts >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {`${pceDeltaPts >= 0 ? '+' : ''}${pceDeltaPts.toFixed(1)} pts`}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Custo Operacional/NVA</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatCurrency(currentCost)}</td>
                  <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatCurrency(futureCost)}</td>
                  <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                    <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${trendClass(costDeltaPct <= 0)}`}>
                      {costDeltaPct <= 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                      {`${costDeltaPct > 0 ? '+' : ''}${costDeltaPct.toFixed(1)}%`}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-r border-y border-slate-200 px-2 py-1.5 font-medium text-slate-700">Capacidade de Entrega</td>
                  <td className="border-r border-y border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{`${Math.round(currentCapacity)}/dia`}</td>
                  <td className="border-r border-y border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{`${Math.round(futureCapacity)}/dia`}</td>
                  <td className="border-y border-slate-200 px-2 py-1.5 text-right">
                    <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${trendClass(capacityDeltaPct >= 0)}`}>
                      {capacityDeltaPct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {`${capacityDeltaPct >= 0 ? '+' : ''}${capacityDeltaPct.toFixed(1)}%`}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="shrink-0 border-t-2 border-slate-800 bg-slate-900 px-4 py-3 text-white">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300">Resumo Executivo de ROI</div>
          <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[11px] text-slate-300">Economia Mensal Estimada</div>
              <div className="font-mono tabular-nums text-lg font-bold text-emerald-300">{formatCurrency(monthlySaving)}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-slate-300">Economia Anual Estimada</div>
              <div className="font-mono tabular-nums text-2xl font-black text-white">{formatCurrency(annualSaving)}</div>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-2 pb-6">
          {!savePromptOpen ? (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => requestSaveConfirmation('close')}
                className="h-7 border border-slate-300 px-3 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => requestSaveConfirmation('apply')}
                className="h-7 border border-blue-700 bg-blue-700 px-3 text-[11px] font-semibold text-white hover:bg-blue-800"
              >
                Aplicar no Mapa
              </button>
            </div>
          ) : (
            <div className="border border-slate-300 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-semibold text-slate-700">
                Deseja salvar esta configuração como um Novo Cenário de Referência?
              </div>
              <div className="mt-2 grid grid-cols-[1fr_auto_auto] gap-2">
                <input
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  placeholder="Nome do Cenário: Ex: Proposta Kaizen v2"
                  className="h-7 border border-slate-300 bg-white px-2 text-[11px] font-mono tabular-nums text-slate-700 outline-none focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleRegisterMilestone}
                  className="h-7 border border-slate-700 bg-slate-700 px-3 text-[11px] font-semibold text-white hover:bg-slate-800"
                >
                  💾 Registrar Marco
                </button>
                <button
                  type="button"
                  onClick={finalizePendingAction}
                  className="h-7 border border-slate-300 bg-white px-3 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Pular
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
